import { ClipboardCheck, AlertTriangle, CheckCircle2, HelpCircle, Satellite, MapPin, Gauge } from 'lucide-react';
import type {
  SprayReconciliation, LabelCompliance, ReconciliationProduct, TankRateCheck,
} from '../../types/spray';

interface ReconciliationPanelProps {
  reconciliation: SprayReconciliation;
  /** Shown before sign-off, so the wording can say "would be" rather than "was". */
  preview?: boolean;
}

const VERDICT_COPY: Record<LabelCompliance, { label: string; tone: string; border: string }> = {
  within_label:  { label: 'Within label rates',      tone: 'text-green-700',   border: 'border-green-300 bg-green-50' },
  above_label:   { label: 'Above label rate',        tone: 'text-red-700',     border: 'border-red-300 bg-red-50' },
  below_label:   { label: 'Below label rate',        tone: 'text-amber-700',   border: 'border-amber-300 bg-amber-50' },
  mixed:         { label: 'Products disagree',       tone: 'text-amber-700',   border: 'border-amber-300 bg-amber-50' },
  unverifiable:  { label: 'Cannot be verified',      tone: 'text-neutral-600', border: 'border-neutral-300 bg-neutral-50' },
};

/**
 * Why a verdict could not be reached. "Unverifiable" is a common and entirely
 * legitimate outcome — an APVMA-imported registration often carries no rate
 * range at all — so it needs an explanation rather than a shrug.
 */
function unverifiableReason(p: ReconciliationProduct): string {
  if (p.label_min_rate == null && p.label_max_rate == null) {
    return 'no registered rate range recorded against this registration';
  }
  if (p.applied_rate == null) {
    return p.dilute
      ? 'no water volume recorded, so there is nothing to divide by'
      : 'no sprayed area to divide by';
  }
  return 'not enough recorded to judge';
}

/**
 * Per-tank water rate, load by load.
 *
 * The headline figures above are the job's average, and an average cannot say
 * WHEN it drifted. "Tanks 1-3 ran 18% wet, tank 4 back in line" is a diagnosis
 * — a nozzle that blocked and was cleared — and it is invisible in one number.
 *
 * Renders nothing unless at least one load was actually judged: a job with no
 * GPS track has a row of "could not check" for every tank, which is a long way
 * of saying nothing.
 */
function TankRateBreakdown({ checks }: { checks: TankRateCheck[] }) {
  const judged = checks.filter(c => c.status !== 'unverifiable');
  if (!judged.length) return null;

  const drifted = judged.filter(c => c.status !== 'ok').length;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Gauge className="h-4 w-4 text-primary-600 shrink-0" />
        <h4 className="text-sm font-medium text-neutral-800">Water rate, tank by tank</h4>
      </div>

      <p className="text-xs text-neutral-500">
        {drifted === 0
          ? `All ${judged.length} checked ${judged.length === 1 ? 'load' : 'loads'} went out within 10% of the calibration.`
          : `${drifted} of ${judged.length} checked ${judged.length === 1 ? 'load' : 'loads'} drifted more than 10% from the calibration.`}
      </p>

      <div className="rounded border border-neutral-200 divide-y divide-neutral-100">
        {checks.map(c => {
          const tone = c.status === 'ok' ? 'text-green-700'
            : c.status === 'unverifiable' ? 'text-neutral-400'
            : Math.abs(c.rate_variance_pct ?? 0) >= 25 ? 'text-red-700' : 'text-amber-700';

          return (
            <div key={c.tank_load_id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-2.5 py-1.5">
              <span className="text-xs font-medium text-neutral-700 shrink-0">
                Tank {c.tank_number}
              </span>

              {c.applied_rate_lpha != null ? (
                <>
                  <span className={`text-xs font-semibold ${tone}`}>
                    {Math.round(c.applied_rate_lpha)} L/ha
                  </span>
                  {c.rate_variance_pct != null && c.status !== 'unverifiable' && (
                    <span className={`text-xs ${tone}`}>
                      {c.rate_variance_pct > 0 ? '+' : ''}{c.rate_variance_pct.toFixed(0)}%
                    </span>
                  )}
                  {c.area_ha != null && (
                    <span className="text-xs text-neutral-400">
                      {c.area_ha.toFixed(2)} ha
                    </span>
                  )}
                </>
              ) : (
                <span className="text-xs text-neutral-400">not checked</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VerdictIcon({ verdict }: { verdict: LabelCompliance }) {
  if (verdict === 'within_label') return <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />;
  if (verdict === 'unverifiable') return <HelpCircle className="h-4 w-4 text-neutral-400 shrink-0" />;
  return <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />;
}

function ProductRow({ p }: { p: ReconciliationProduct }) {
  const rateSuffix = p.dilute ? `${p.unit}/100L` : `${p.unit}/ha`;

  const range = p.label_min_rate != null || p.label_max_rate != null
    ? `${p.label_min_rate ?? '—'}–${p.label_max_rate ?? '—'} ${p.rate_unit}`
    : null;

  return (
    <div className="py-2.5 border-t border-neutral-100 first:border-t-0">
      <div className="flex items-start gap-2">
        <VerdictIcon verdict={p.verdict} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-neutral-800">{p.product_name}</p>

          {p.applied_rate != null ? (
            <p className="text-xs text-neutral-600 mt-0.5">
              Applied at <strong>{p.applied_rate} {rateSuffix}</strong>
              {range && <> · label {range}</>}
            </p>
          ) : (
            <p className="text-xs text-neutral-500 mt-0.5">
              No applied rate — {unverifiableReason(p)}
            </p>
          )}

          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-neutral-500">
            {p.declared_total != null && (
              <span>Declared: {p.declared_total} {p.unit}</span>
            )}
            {p.computed_total != null && (
              <span>Rate implies: {p.computed_total} {p.unit}</span>
            )}
          </div>

          {p.variance_flag && p.variance_pct != null && (
            <p className="text-xs text-amber-600 mt-1">
              {p.variance_pct > 0 ? '+' : ''}{p.variance_pct}% against what the rate implies —
              worth checking whether the measure or the tank record is out.
            </p>
          )}

          {p.total_source === 'computed' && (
            <p className="text-xs text-neutral-400 mt-1">
              Nothing was declared for this product, so the figures come from the planned rate.
            </p>
          )}

          {p.unconvertible_entries > 0 && (
            <p className="text-xs text-amber-600 mt-1">
              {p.unconvertible_entries} tank {p.unconvertible_entries === 1 ? 'entry uses a unit' : 'entries use units'}
              {' '}that cannot be converted to {p.unit} — excluded from the total.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * What actually went out, and whether it sits inside the label.
 *
 * The stored verdict is deliberately frozen at completion — a label rate
 * revised next season must not silently rewrite last season's compliance
 * record — so this component renders what was computed on the day rather than
 * recomputing anything.
 */
function ReconciliationPanel({ reconciliation: r, preview = false }: ReconciliationPanelProps) {
  const verdict = VERDICT_COPY[r.label_compliance];

  const areaNote = r.actual_area_source === 'gps'
    ? 'measured from the GPS track'
    : r.actual_area_source === 'manual'
      ? 'entered by hand'
      : 'the planned area — no GPS track to measure against';

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-5 w-5 text-primary-600" />
        <h3 className="font-medium text-sm text-neutral-800">
          {preview ? 'What this will record' : 'Applied rate check'}
        </h3>
      </div>

      <div className={`rounded-lg border px-3 py-2 ${verdict.border}`}>
        <p className={`text-sm font-semibold ${verdict.tone}`}>{verdict.label}</p>
        {r.label_compliance === 'mixed' && (
          <p className="text-xs text-neutral-600 mt-0.5">
            One product went out above its label rate and another below. Both are listed underneath.
          </p>
        )}
        {r.label_compliance === 'unverifiable' && (
          <p className="text-xs text-neutral-600 mt-0.5">
            No registered rate range was recorded for these products, so there is nothing to check
            the applied rate against. This is normal for registrations imported without rates.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div>
          <p className="text-xs text-neutral-500">Tanks</p>
          <p className="font-semibold text-neutral-800">{r.tanks_used}</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500">Water</p>
          <p className="font-semibold text-neutral-800">{r.total_water_l.toFixed(0)} L</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500">Area sprayed</p>
          <p className="font-semibold text-neutral-800">
            {r.actual_area_ha != null ? `${r.actual_area_ha.toFixed(2)} ha` : '—'}
          </p>
        </div>
        {r.planned_area_ha != null && (
          <div>
            <p className="text-xs text-neutral-500">Planned area</p>
            <p className="font-semibold text-neutral-800">{r.planned_area_ha.toFixed(2)} ha</p>
          </div>
        )}
      </div>

      <p className="text-xs text-neutral-500 flex items-center gap-1">
        {r.actual_area_source === 'gps'
          ? <Satellite className="h-3 w-3 shrink-0" />
          : <MapPin className="h-3 w-3 shrink-0" />}
        Area is {areaNote}
        {r.coverage?.covered_pct != null && ` · ${r.coverage.covered_pct}% of the target area was covered`}
      </p>

      <div className="rounded border border-neutral-200 px-3 py-1">
        {r.products.map(p => <ProductRow key={p.chemical_id} p={p} />)}
      </div>

      {r.tank_checks && r.tank_checks.length > 0 && (
        <TankRateBreakdown checks={r.tank_checks} />
      )}

      {r.tanks_used === 0 && (
        <p className="text-xs text-neutral-500">
          No tank loads were recorded, so the totals come from the planned rate rather than from
          anything measured. Recording tanks as they are mixed makes this a real check.
        </p>
      )}
    </div>
  );
}

export default ReconciliationPanel;
